import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { hasAuthError } from './lib/authErrorParams';
import HomePage from './pages/HomePage';
import RegistrationPage from './pages/RegistrationPage';
import LoginPage from './pages/LoginPage';
import AccountPage from './pages/AccountPage';
import VolunteerPage from './pages/VolunteerPage';
import PaymentPage from './pages/PaymentPage';
import EmailHelpPage from './pages/EmailHelpPage';
import NotAuthorized from './pages/NotAuthorized';
import RequireAuth from './components/Auth/RequireAuth';
import { Analytics } from '@vercel/analytics/react';

// Admin routes are code-split: anonymous/member visitors don't download
// admin tooling (and its heavy deps like xlsx) in the main entry bundle.
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminInterests = lazy(() => import('./pages/Admin/AdminInterests'));
const AdminVolunteerHours = lazy(() => import('./pages/Admin/AdminVolunteerHours'));
const AdminMembers = lazy(() => import('./pages/Admin/AdminMembers'));
const AdminPayments = lazy(() => import('./pages/Admin/AdminPayments'));
const AdminEvents = lazy(() => import('./pages/Admin/AdminEvents'));
const AdminFunctions = lazy(() => import('./pages/Admin/AdminFunctions'));
const AdminAttendance = lazy(() => import('./pages/Admin/AdminAttendance'));
const ActBlueWizardTest = lazy(() => import('./pages/Admin/ActBlueWizardTest'));
const AdminReconciliation = lazy(() => import('./pages/Admin/AdminReconciliation'));
const AdminReports = lazy(() => import('./pages/Admin/AdminReports'));
const NameBadges = lazy(() => import('./pages/Admin/NameBadges'));

/**
 * When a magic link is expired/already-used, Supabase redirects to the Site URL
 * ROOT with an error hash (e.g. `/#error=access_denied&error_code=otp_expired`),
 * NOT to `/account`. captureAuthError() (run in main.tsx) snapshots it before the
 * hash is stripped; this sends the user to /login — where the friendly message
 * and "request a new link" live — regardless of which route they landed on.
 * Runs once on first mount; renders nothing.
 */
function AuthErrorRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const [handled, setHandled] = useState(false);

  useEffect(() => {
    if (handled) return;
    setHandled(true);
    if (hasAuthError() && location.pathname !== '/login') {
      navigate('/login', { replace: true });
    }
  }, [handled, location.pathname, navigate]);

  return null;
}

function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div
        className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthErrorRedirect />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/register" element={<RegistrationPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
          <Route path="/volunteer" element={<VolunteerPage />} />
          <Route path="/volunteer/log" element={<VolunteerPage />} />
          <Route path="/admin" element={<RequireAuth adminOnly><AdminDashboard /></RequireAuth>} />
          <Route path="/admin/interests" element={<RequireAuth adminOnly><AdminInterests /></RequireAuth>} />
          <Route path="/admin/volunteer-hours" element={<RequireAuth adminOnly><AdminVolunteerHours /></RequireAuth>} />
          <Route path="/admin/attendance" element={<RequireAuth adminOnly><AdminAttendance /></RequireAuth>} />
          <Route path="/admin/members" element={<RequireAuth adminOnly><AdminMembers /></RequireAuth>} />
          <Route path="/admin/badges" element={<RequireAuth adminOnly><NameBadges /></RequireAuth>} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/admin/payments" element={<RequireAuth adminOnly><AdminPayments /></RequireAuth>} />
          <Route path="/admin/events" element={<RequireAuth adminOnly><AdminEvents /></RequireAuth>} />
          <Route path="/admin/functions" element={<RequireAuth adminOnly><AdminFunctions /></RequireAuth>} />
          <Route path="/admin/functions/wizard-test" element={<RequireAuth adminOnly actBlueOnly><ActBlueWizardTest /></RequireAuth>} />
          <Route path="/admin/reconciliation" element={<RequireAuth adminOnly actBlueOnly><AdminReconciliation /></RequireAuth>} />
          <Route path="/admin/reports" element={<RequireAuth adminOnly><AdminReports /></RequireAuth>} />
          <Route path="/email-help" element={<EmailHelpPage />} />
          <Route path="/not-authorized" element={<NotAuthorized />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <Analytics />
    </BrowserRouter>
  );
}

export default App;
